import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { ChevronLeft, ChevronRight, Activity } from "lucide-react";
import { useStore } from "@/store/useStore";
import { Button } from "@/components/Button";
import { cn } from "@/lib/cn";
import type { ID } from "@/types";

// before/after stance slider (§10.4).
// produces belief-change data without declaring winners.

export function StanceSlider({ strainId, versionId }: { strainId: ID; versionId: ID }) {
  const { getStance, setStance } = useStore();
  const existing = getStance(strainId);
  const [before, setBefore] = useState(existing?.before_score ?? 50);
  const [after, setAfter] = useState(existing?.after_score ?? 50);
  const [note, setNote] = useState(existing?.note ?? "");
  const [submitted, setSubmitted] = useState(!!existing);
  const [step, setStep] = useState<"before" | "after" | "note">(existing ? "note" : "before");

  // when user moves the before slider, default after to before
  useEffect(() => { if (step === "before") setAfter(before); }, [before, step]);

  const moved = after - before;
  const movedColor = moved > 0 ? "var(--color-support)" : moved < 0 ? "var(--color-rebut)" : "var(--color-ink-muted)";

  const submit = () => {
    setStance(strainId, versionId, before, after, note.trim() || undefined);
    setSubmitted(true);
  };

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="flex items-center gap-2 mb-1">
        <Activity size={14} className="text-[var(--color-inferred)]" />
        <div className="text-[13px] font-semibold text-[var(--color-ink)]">Stance survey</div>
        <div className="ml-auto text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-faint)]">no likes — just movement</div>
      </div>
      <p className="text-[12px] text-[var(--color-ink-muted)] mb-4 leading-relaxed">
        Where did you stand before reading this strain, and where do you stand now? This feeds belief-change signals, not a winner.
      </p>

      {/* before */}
      <SliderRow
        label="before reading"
        value={before}
        onChange={setBefore}
        disabled={step !== "before" && submitted}
        color="var(--color-ink-muted)"
      />

      {/* after */}
      <div className="mt-3">
        <SliderRow
          label="after reading"
          value={after}
          onChange={setAfter}
          disabled={step === "before"}
          color={movedColor}
        />
      </div>

      {/* delta */}
      <div className="mt-3 flex items-center justify-between rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)]/60 px-3 py-2">
        <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">belief change</span>
        <span className="font-mono text-sm font-semibold" style={{ color: movedColor }}>
          {moved > 0 ? "+" : ""}{moved}
        </span>
      </div>

      {/* note */}
      {step !== "before" && (
        <div className="mt-3">
          <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">what moved? (optional)</label>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="the selection-effect undercut, mostly"
            className="mt-1.5 w-full h-9 px-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] text-[13px] text-[var(--color-ink)] placeholder:text-[var(--color-ink-faint)]"
          />
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        {step === "before" ? (
          <Button size="sm" onClick={() => setStep("after")} rightIcon={<ChevronRight size={13} />}>
            Set my before →
          </Button>
        ) : (
          <Button size="sm" onClick={submit} disabled={submitted} leftIcon={<Activity size={13} />}>
            {submitted ? "Recorded" : "Record stance"}
          </Button>
        )}
      </div>

      {submitted && (
        <div className="mt-3 text-[11px] text-[var(--color-ink-faint)] flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--color-support)]" />
          Your stance is recorded. This contributes to the strain's belief-change signal, not a leaderboard.
        </div>
      )}
    </div>
  );
}

function SliderRow({
  label, value, onChange, disabled, color,
}: {
  label: string; value: number; onChange: (v: number) => void; disabled?: boolean; color: string;
}) {
  const pct = value;
  return (
    <div className={cn(disabled && "opacity-70")}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">{label}</span>
        <span className="font-mono text-xs font-semibold" style={{ color }}>{value}</span>
      </div>
      <div className="relative h-8 flex items-center">
        {/* track */}
        <div className="absolute inset-x-0 h-1.5 rounded-full bg-[var(--color-surface-3)] overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ background: color }}
            animate={{ width: `${pct}%` }}
            transition={{ type: "spring", stiffness: 200, damping: 24 }}
          />
        </div>
        {/* disagree / agree anchors */}
        <div className="absolute left-0 -bottom-0.5 text-[9px] font-mono text-[var(--color-rebut)]">disagree</div>
        <div className="absolute right-0 -bottom-0.5 text-[9px] font-mono text-[var(--color-support)]">agree</div>
        {/* thumb */}
        <input
          type="range"
          min={0}
          max={100}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(Number(e.target.value))}
          className="relative w-full appearance-none bg-transparent z-10 cursor-pointer
            [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full
            [&::-webkit-slider-thumb]:bg-[var(--color-ink)] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[var(--color-primary)]
            [&::-webkit-slider-thumb]:shadow-[0_0_0_4px_rgba(94,234,212,0.15)] [&::-webkit-slider-thumb]:cursor-grab
            [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[var(--color-primary)] [&::-moz-range-thumb]:bg-[var(--color-ink)]"
        />
      </div>
    </div>
  );
}
